import { format, parseISO, differenceInMilliseconds, formatDistance, intervalToDuration, formatDuration as formatDurationParts } from 'date-fns';
import { es, enUS, ptBR } from 'date-fns/locale';
import i18next from 'i18next';

const localeMap = {
  es,
  'es-ES': es,
  en: enUS,
  'en-US': enUS,
  pt: ptBR,
  'pt-BR': ptBR,
};

const getDateFnsLocale = () => {
  const language = i18next?.language;
  if (!language) return undefined;
  if (localeMap[language]) return localeMap[language];
  const shortLang = language.split('-')[0];
  return localeMap[shortLang];
};

/**
 * Returns the current date as a string in "YYYY-MM-DD" format.
 * Uses local time.
 */
export const getTodayDateString = () => {
  const now = new Date();
  const year = now.getFullYear();
  const month = String(now.getMonth() + 1).padStart(2, '0');
  const day = String(now.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

/**
 * Formats a date object or string into a readable string.
 * Default format: "dd/MM/yyyy"
 */
export const formatDateTime = (date, formatStr) => {
  if (!date) return '';
  const dateObj = typeof date === 'string' ? parseISO(date) : date;
  const locale = getDateFnsLocale();
  const resolvedFormat = formatStr || 'P';
  return format(dateObj, resolvedFormat, { locale });
};

/**
 * Parses a "YYYY-MM-DD" string into a Date object.
 * IMPORTANT: This creates a Date object set to midnight LOCAL time.
 * This avoids the UTC conversion issue where "2023-01-01" becomes "2022-12-31T21:00:00" in UTC-3.
 * Use this when you need a Date object for UI components (like Material-UI Pickers)
 * but the source value is a plain date string.
 */
export const parsePlainDate = dateString => {
  if (!dateString) return null;
  // Split YYYY-MM-DD and create date manually to preserve local day
  const [year, month, day] = dateString.split('-').map(Number);
  return new Date(year, month - 1, day);
};

/**
 * Formats a "YYYY-MM-DD" (or ISO) string safely using local midnight.
 * Falls back to empty string when the input cannot be parsed.
 */
export const formatPlainDateDisplay = (dateString, formatStr) => {
  if (!dateString) return '';
  const sanitizedDate = dateString.split('T')[0];
  const date = parsePlainDate(sanitizedDate);
  if (!date) return '';
  const locale = getDateFnsLocale();
  const resolvedFormat = formatStr || 'P';
  return format(date, resolvedFormat, { locale });
};

/**
 * Converts a Date object to a "YYYY-MM-DD" string.
 * Uses local time to extract the date parts.
 */
export const toPlainDateString = date => {
  if (!date) return null;
  if (typeof date === 'string') {
    return date.substring(0, 10);
  }
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

/**
 * Takes a "YYYY-MM-DD" string and returns the next day in the same format.
 * Returns null when the input cannot be parsed.
 */
export const getNextDayPlainDateString = dateString => {
  if (!dateString) return null;
  const sanitizedDate = dateString.split('T')[0];
  const date = parsePlainDate(sanitizedDate);
  if (!date) return null;
  date.setDate(date.getDate() + 1);
  return toPlainDateString(date);
};

/**
 * Calculates the duration between two dates.
 * Returns a human-readable string (e.g., "5 minutes ago").
 */
export const calculateDuration = (start, end = new Date()) => {
  const startDate = typeof start === 'string' ? parseISO(start) : start;
  const endDate = typeof end === 'string' ? parseISO(end) : end;
  return formatDistance(startDate, endDate, { addSuffix: true, locale: getDateFnsLocale() });
};


/**
 * Formats an absolute duration (in milliseconds) into a localized string.
 * Examples: "2 minutes", "1 hour 5 minutes", "3 días"
 */
export const formatDurationFromMilliseconds = milliseconds => {
  if (typeof milliseconds !== 'number' || !Number.isFinite(milliseconds) || milliseconds < 0) {
    return '';
  }

  const locale = getDateFnsLocale();
  const durationShape = intervalToDuration({ start: 0, end: milliseconds });
  const result = formatDurationParts(durationShape, {
    format: ['days', 'hours', 'minutes', 'seconds'],
    locale,
    zero: false,
  });

  if (result) {
    return result;
  }

  // When every component is zero, formatDuration returns an empty string unless zero=true
  return formatDurationParts({ seconds: 0 }, { locale, zero: true });
};

/**
 * Formats the elapsed time between two instants into a localized duration string.
 */
export const formatDurationBetweenDates = (start, end) => {
  if (!start || !end) {
    return '';
  }

  const startDate = typeof start === 'string' ? parseISO(start) : start;
  const endDate = typeof end === 'string' ? parseISO(end) : end;

  if (!(startDate instanceof Date) || isNaN(startDate) || !(endDate instanceof Date) || isNaN(endDate)) {
    return '';
  }

  const diff = Math.abs(differenceInMilliseconds(endDate, startDate));
  return formatDurationFromMilliseconds(diff);
};

/**
 * Periods.
 *
 * A period is a year-month pair: `{ year, month }` with month in 1..12.
 * Its wire format — used by every table filter and by form inputs — is "YYYY-MM".
 * Its display format is "YYYY/MM".
 */

export const formatPeriod = ({ year, month }) => `${year}-${String(month).padStart(2, '0')}`;

/**
 * Parses "YYYY-MM" into `{ year, month }`, or null when the value is absent or
 * malformed. The month range is enforced by the pattern rather than by a check,
 * so this is the one place that decides whether a period is present at all —
 * see PeriodSelection for the polymorphic handling of the two outcomes.
 */
export const parsePeriod = value => {
  const match = /^(\d{4})-(0[1-9]|1[0-2])$/.exec(value || '');
  return match && { year: Number(match[1]), month: Number(match[2]) };
};

/**
 * Parses the period out of a value that may carry a day too — the API returns
 * a period's `date` as "YYYY-MM-DD" but accepts "YYYY-MM" on the way back in.
 */
export const parsePeriodFromDateString = value => parsePeriod((value || '').slice(0, 7));

export const formatPeriodDisplay = period => formatPeriod(period).replace('-', '/');

/** Negative when a is earlier than b, positive when later, 0 when equal. */
export const comparePeriods = (a, b) => a.year - b.year || a.month - b.month;

export const addMonthsToPeriod = ({ year, month }, amount) => {
  const monthsFromYearZero = year * 12 + (month - 1) + amount;
  return { year: Math.floor(monthsFromYearZero / 12), month: (monthsFromYearZero % 12) + 1 };
};

export const periodFromDate = date => ({ year: date.getFullYear(), month: date.getMonth() + 1 });
